import * as THREE from "three";

/* ===============================================================
   STEP 0 : 유틸 함수들
   --------------------------------------------------------------- */

// 👉 Object3D or Intersection 결과에서 실제 object 추출
function toObject(target) {
  return target?.object || target || null;
}

// 👉 material 단일/배열 여부 상관없이 배열로 반환
function getMats(obj) {
  const m = obj?.material;
  return Array.isArray(m) ? m : m ? [m] : [];
}

/* ===============================================================
   📦 createPointerHandlers
   - Hover / Move / Click / Marker / Drag(회전/이동) 처리 총괄
   - bindPointerRaycast에서 호출됨
   =============================================================== */
export function createPointerHandlers({
  mount,
  camera,
  getTargets,
  getMesh,
  meshRef,
  highlightColor = 0x222222,
  useWeakMap = true,
  setIsMarked,
  followMeshScale = true,
} = {}) {
  if (!mount || !camera || !getTargets || !getMesh) {
    throw new Error(
      "createPointerHandlers: mount/camera/getTargets/getMesh 필요"
    );
  }

  /* ===============================================================
     STEP 1 : Hover 하이라이트 상태 저장/복원 로직
     --------------------------------------------------------------- */

  // 👉 이전 emissive 값을 저장할 store(WeakMap 또는 userData)
  const store = useWeakMap ? new WeakMap() : null;

  // ===============================================================
  // STEP 1-1 : 현재 emissive(발광색) 값을 저장해두는 함수
  // ---------------------------------------------------------------
  // 👉 왜 필요한가?
  //    - Hover 상태에서 오브젝트의 emissive 색을 강조색으로 바꿔버리는데,
  //      Hover가 끝나면 "원래 색"으로 되돌려야 함.
  //    - 그러려면 하이라이트 적용 전에 기존 emissive 색을 기억해둬야 한다.
  //    - 이 함수는 그 "원래 색"을 저장하는 역할.
  //
  // 👉 어떻게 저장하는가?
  //    - obj.material 이 하나일 수도 있고, 배열일 수도 있어서
  //      getMats(obj) 로 항상 배열 형태로 만든다.
  //    - 각 재질의 emissive.getHex() 값을 prev 배열에 저장한다.
  //    - 저장 장소는 WeakMap 또는 userData 중 선택해서 넣는다.
  // ===============================================================
  const savePrev = (obj) => {
    // obj.material → 항상 배열 형태로 얻기
    const mats = getMats(obj);

    // 현재 emissive 색상(hex 값)을 배열로 저장
    // ex) [0x000000, 0x222222, null, ...]
    const prev = mats.map((m) => (m?.emissive ? m.emissive.getHex() : null));

    // 저장 장소 선택 (WeakMap 또는 userData)
    // WeakMap은 obj가 사라지면 자동 삭제되어 메모리 누수 방지
    if (useWeakMap) {
      // WeakMap 에서 key = obj, value = prev 배열
      store.set(obj, prev);
    } else {
      // userData 방식: 오브젝트 내부에 직접 저장
      obj.userData = obj.userData || {};
      obj.userData.__hoverPrevEmissive = prev;
    }
  };

  // ===============================================================
  // STEP 1-2 : savePrev 에서 저장해둔 emissive 색을 다시 복원하는 함수
  // ---------------------------------------------------------------
  // 👉 왜 필요한가?
  //    - Hover가 끝났을 때(마우스가 떠났을 때)
  //      highlightColor 로 바꾼 emissive 값을 원래 값으로 돌려야 하기 때문.
  //
  // 👉 어떤 방식으로 복원하는가?
  //    1) 저장해둔 prev 배열을 WeakMap 또는 userData에서 꺼냄
  //    2) 현재 obj.material 배열과 prev 배열을 같은 index로 매칭하여
  //       각 material.emissive 색을 setHex(prev[i]) 로 되돌림
  //    3) prev 를 정상 복원하면 저장 데이터도 삭제함 (청소)
  // ===============================================================
  const loadPrev = (obj) => {
    const mats = getMats(obj);

    // prev에 저장된 원래 emissive 배열 꺼내기
    const prev = useWeakMap
      ? store.get(obj) // WeakMap 사용 시
      : obj.userData?.__hoverPrevEmissive; // userData 사용 시

    // prev 값이 있고, length도 일치하면 정상 복원 가능
    if (prev && prev.length === mats.length) {
      mats.forEach((m, i) => {
        const hex = prev[i];
        // emissive가 존재하고 원래 hex 값도 있으면 setHex로 복원
        if (m?.emissive && hex != null) m.emissive.setHex(hex);
      });

      // prev 값이 없으면 억지로 기본값(0x000000)으로 리셋
    } else {
      mats.forEach((m) => m?.emissive?.setHex(0x000000));
    }

    // 저장된 prev를 삭제하여 메모리 정리
    if (useWeakMap) {
      store.delete(obj);
    } else if (obj.userData) {
      delete obj.userData.__hoverPrevEmissive;
    }
  };

  // ===============================================================
  // STEP 1-3 : 하이라이트 적용 (Hover 들어올 때 실행됨)
  // ---------------------------------------------------------------
  // 👉 이 함수가 하는 일
  //    1) savePrev(obj) 를 호출해서 "원래 emissive 색"을 저장해두고
  //    2) obj.material들의 emissive 색을 highlightColor 로 변경하여
  //       마우스 hover 했다는 시각적 효과를 준다.
  //
  // 👉 즉, 이 함수는
  //         "원래 색 백업 + 하이라이트 적용"
  //    이 두 가지를 동시에 수행하는 핵심 함수.
  // ===============================================================
  const applyHighlight = (obj) => {
    // 먼저 원래 emissive 상태 저장
    savePrev(obj);

    // 하이라이트 색 적용
    getMats(obj).forEach((m) => {
      if (m?.emissive) m.emissive.setHex(highlightColor);
    });
  };

  /* ===============================================================
     STEP 2 : Marker 관련 상태 및 DOM 생성/위치 업데이트
     --------------------------------------------------------------- */

  const raycaster = new THREE.Raycaster();
  const pointerNDC = new THREE.Vector2();
  let marker = null;
  let rafId = 0;

  // STEP 2-1 : 마커 DOM 생성
  const makeMarkerDOM = () => {
    const e = document.createElement("div");
    e.className = "marker";
    e.style.position = "absolute";
    e.style.left = "0";
    e.style.top = "0";
    e.style.width = "30px";
    e.style.height = "30px";
    e.style.borderRadius = "50%";
    e.style.background = "#ff009d2a";
    e.style.border = "2px solid rgb(57,255,20)";
    e.style.pointerEvents = "auto";
    e.style.cursor = "pointer";
    e.style.zIndex = "1";
    return e;
  };

  // STEP 2-2 : requestAnimationFrame 루프 시작 (마커 위치 갱신)
  const startLoop = () => {
    if (rafId) return;

    const tick = () => {
      rafId = requestAnimationFrame(tick);
      if (!marker) return;

      const rect = mount.getBoundingClientRect();

      // 로컬좌표 → 월드 → 카메라 NDC 변환
      const world = marker.localPos.clone();
      marker.obj.localToWorld(world);
      const proj = world.clone().project(camera);

      // 화면 밖이면 숨기기
      const hidden = proj.z < -1 || proj.z > 1;
      marker.dom.style.display = hidden ? "none" : "block";

      // NDC → Pixel 위치 변환
      const x = (proj.x * 0.5 + 0.5) * rect.width;
      const y = (-proj.y * 0.5 + 0.5) * rect.height;

      marker.dom.style.transform = `translate(${Math.round(x)}px, ${Math.round(
        y
      )}px) translate(-50%, -50%)`;
    };

    rafId = requestAnimationFrame(tick);
  };

  // STEP 2-3 : 루프 종료
  const stopLoop = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
  };

  // STEP 2-4 : 마커 제거
  const removeMarker = (centerMesh = true) => {
    if (marker) {
      marker.dom.remove();
      marker = null;
      setIsMarked(false);
    }
    stopLoop();

    // Mesh 위치 초기화
    if (centerMesh) {
      const mesh = getMesh();
      mesh?.position.set(0, 0, 0);
    }
  };

  /* ===============================================================
     STEP 3 : Hover 이벤트 핸들러
     --------------------------------------------------------------- */

  // STEP 3-1 : Hover 진입
  const onEnter = (target) => {
    const obj = toObject(target);
    if (!obj) return;
    mount.style.cursor = "pointer";
    applyHighlight(obj);
  };

  // STEP 3-2 : Hover 이동 (월드/로컬 좌표 계산)
  const onMove = (hit) => {
    if (!hit) return;
    const obj = toObject(hit);
    if (!obj) return;

    const worldPos = hit.point.clone();
    const localPos = obj.worldToLocal(worldPos.clone());

    console.log("Pointer 이동 중 → 월드 좌표:", worldPos);
    console.log("Pointer 이동 중 → 로컬 좌표:", localPos);
  };

  // STEP 3-3 : Hover 종료
  const onLeave = (target) => {
    const obj = toObject(target);
    if (!obj) return;
    mount.style.cursor = "default";
    loadPrev(obj);
  };

  // STEP 3-4 : Reset(hover 초기화용)
  const reset = (target) => {
    const obj = toObject(target);
    if (obj) loadPrev(obj);
    mount.style.cursor = "default";
  };

  /* ===============================================================
     STEP 4 : Click → Marker 생성 / 제거
     --------------------------------------------------------------- */

  const onClick = (e) => {
    // 마커 클릭 자체는 무시
    if (e.target instanceof Element && e.target.closest(".marker")) return;

    // STEP 4-1 : Pixel → NDC 변환
    const r = mount.getBoundingClientRect();
    pointerNDC.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -(((e.clientY - r.top) / r.height) * 2 - 1)
    );

    const targets = getTargets() || [];
    if (!targets.length) return;

    // STEP 4-2 : Raycast 수행
    //  나중에 필요시 활성화
    raycaster.setFromCamera(pointerNDC, camera);
    const hit = raycaster.intersectObjects(targets, true)[0];
    if (!hit) return;

    const obj = hit.object;
    const localPos = obj.worldToLocal(hit.point.clone());

    // 기존 마커 제거
    if (marker) removeMarker(false);

    // STEP 4-3 : 새 마커 DOM 생성
    const dom = makeMarkerDOM();
    dom.addEventListener("click", (ev) => {
      ev.stopPropagation();
      removeMarker(true);
    });

    mount.appendChild(dom);
    setIsMarked(true);

    // Marker 정보 객체 저장
    marker = { dom, obj, localPos };

    // STEP 4-4 : 위치 갱신 루프 시작
    startLoop();

    // STEP 4-5 : Mesh(왼쪽) 시프트 이동
    const mesh = meshRef.current;
    if (mesh) {
      const camRight = new THREE.Vector3();
      camera.matrixWorld.extractBasis(
        camRight,
        new THREE.Vector3(),
        new THREE.Vector3()
      );
      camRight.normalize();

      const leftOffset = camRight.clone().multiplyScalar(-0.35);
      mesh.position.copy(leftOffset);
      mesh.updateMatrixWorld();
    }
  };

  /* ===============================================================
     STEP 5 : Drag(회전/이동) 처리
     --------------------------------------------------------------- */

  let isDragging = false;
  let lastX = 0,
    lastY = 0;

  const onPointerDown = (e) => {
    isDragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
  };

  const onPointerUp = () => {
    isDragging = false;
  };

  const onPointerMoveDrag = (e) => {
    if (!isDragging) return;
    const mesh = meshRef.current;
    if (!mesh) return;

    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;

    // STEP 5-1 : 좌클릭 → 회전
    if (e.buttons === 1) {
      mesh.rotation.y += dx * 0.001;
      mesh.rotation.x += dy * 0.001;

      // STEP 5-2 : 우클릭 → 이동
    } else if (e.buttons === 2) {
      mesh.position.x += dx * 0.001;
      mesh.position.y -= dy * 0.001;
    }
  };

  // mount에 직접 드래그 등록
  mount.addEventListener("pointermove", onPointerMoveDrag);

  /* ===============================================================
     STEP 6 : Dispose(해제)
     --------------------------------------------------------------- */

  const dispose = () => {
    removeMarker(true);
    mount.removeEventListener("pointermove", onPointerMoveDrag);
  };

  /* ===============================================================
     RETURN : bindPointerRaycast에서 사용할 인터페이스
     --------------------------------------------------------------- */
  return {
    onEnter,
    onMove,
    onLeave,
    onClick,
    onPointerDown,
    onPointerUp,
    reset,
    dispose,
  };
}
